import { defineStore } from 'pinia'
import { apiClient } from '@/helpers/api'
import { handleApiError } from '@/services/errorHandler'
import { CHANNELS_BY_TYPE, JOB_PROGRESS_MESSAGES, NOTIFICATIONS_PREVIEW_MESSAGES } from '@/constants'
import { useUserStore } from '@/stores/UserStore'

export const useNotificationsStore = defineStore('NotificationsStore', {
  state: () => ({
    notifications: null,
    inprogressJobsWithInfo: null,
    notificationsLoading: false,
    inprogressJobsLoading: false,
    unreadNotificationsCount: 0,
    notificationsPreviews: null,
    nextPageUrl: null,
    prevPageUrl: null,
    lastPage: 0,
  }),

  getters: {
    preparedInprogressJobs() {
      const userStore = useUserStore()

      if (!this.inprogressJobsWithInfo) return []

      const parsedJobInfo = JSON.parse(userStore.user.job_info)

      return Object.keys(this.inprogressJobsWithInfo).reduce((acc, key) => {
        const job = this.inprogressJobsWithInfo[key]
        if (job.active) {
          const splittedKey = key.split('-')
          const jobChannel = splittedKey[splittedKey.length - 1]

          acc.push({
            ...parsedJobInfo[key],
            channel: CHANNELS_BY_TYPE[jobChannel]?.channel,
            message: JOB_PROGRESS_MESSAGES[key],
          })
        }
        return acc
      }, [])
    },

    preparedNotificationsPreviews() {
      if (!this.notificationsPreviews) return []

      // If all nested `count` values are 0 (or missing), return an empty list
      const hasNonZeroCount = Object.keys(this.notificationsPreviews).some(key => {
        const preview = this.notificationsPreviews[key]

        // Skip non-object entries like `unread_count`
        if (!preview || typeof preview !== 'object') return false

        return Object.keys(preview).some(item => {
          if (item === 'total') return false
          const nested = preview[item] || {}
          const count = typeof nested.count === 'number' ? nested.count : 0
          return count > 0
        })
      })

      if (!hasNonZeroCount) return []

      return Object.keys(this.notificationsPreviews).reduce((acc, key) => {
        const preview = this.notificationsPreviews[key]

        Object.keys(preview).forEach(item => {
          if (item !== 'total' && preview[item].count > 0) {
            acc.push({
              message: NOTIFICATIONS_PREVIEW_MESSAGES[key]?.label,
              description: NOTIFICATIONS_PREVIEW_MESSAGES[key]?.message(preview[item].count, CHANNELS_BY_TYPE[item]?.channel?.name),
              count: preview[item].count,
              images: preview[item].images || [],
              channel: CHANNELS_BY_TYPE[item]?.channel,
            })
          }
        })
        return acc
      }, [])
    },

    preparedNotifications() {
      if (!this.notifications) return []

      return this.notifications.data.reduce((acc, notification) => {
        acc.push({
          id: notification.id,
          description: notification.description,
          message: notification.title,
          isRead: notification.is_read,
          createdAt: notification.created_at,
          images: notification.images || [],
          channel: CHANNELS_BY_TYPE[notification.type.split(':')[1]]?.channel,
          type: notification.type,
        })
        return acc
      }, [])
    },

    inprogressJobsLength() {
      const userStore = useUserStore()
      if (!userStore.user?.job_info) return 0

      const parsedJobInfo = JSON.parse(userStore.user.job_info)

      return Object.keys(parsedJobInfo).reduce((acc, key) => {
        const job = JSON.parse(userStore.user.job_info)[key]
        if (job.active) {
          acc.push(key)
        }
        return acc
      }, []).length
    },
  },

  actions: {
    async fetchInprogressJobs() {
      try {
        this.inprogressJobsLoading = true
        const { data } = await apiClient().get('/users/me/job-info')

        this.inprogressJobsWithInfo = data
        this.inprogressJobsLoading = false
      } catch (error) {
        handleApiError(error, { component: 'NotificationsStore', action: 'fetchInprogressJobs' })
        this.inprogressJobsLoading = false
      }
    },

    async clearAllNotifications() {
      try {
        await apiClient().delete('/notifications/delete-all')
        this.notifications = null
        this.notificationsPreviews = null
        this.unreadNotificationsCount = 0
      } catch (error) {
        handleApiError(error, { component: 'NotificationsStore', action: 'clearAllNotifications' })
      }
    },

    async deleteNotification(id) {
      try {
        await apiClient().delete(`/notifications/delete`, { data: { notification_ids: [id] } })

        this.notifications = {
          ...this.notifications,
          data: this.notifications.data.filter(notification => notification.id !== id)
        }

        this.unreadNotificationsCount = this.unreadNotificationsCount - 1

      } catch (error) {
        handleApiError(error, { component: 'NotificationsStore', action: 'deleteNotification' })
      }
    },

    async markAllAsRead() {
      try {
        await apiClient().post('/notifications/mark-all-as-read')

        this.notifications = {
          ...this.notifications,
          data: this.notifications.data.map(notification => ({ ...notification, is_read: true }))
        }

        this.unreadNotificationsCount = 0
      } catch (error) {
        handleApiError(error, { component: 'NotificationsStore', action: 'markAllAsRead' })
      }
    },

    async markAsRead(id) {
      try {
        await apiClient().post(`/notifications/mark-as-read`, { notification_ids: [id] })

        this.notifications = {
          ...this.notifications,
          data: this.notifications.data.map(notification => 
            notification.id === id 
              ? { ...notification, is_read: true }
              : notification
          )
        }
      } catch (error) {
        handleApiError(error, { component: 'NotificationsStore', action: 'markAsRead' })
      }
    },

    async fetchUnreadNotificationsCount() {
      try {
        const { data } = await apiClient().get('/notifications/unread-count')
        this.unreadNotificationsCount = data?.unread_count
      } catch (error) {
        handleApiError(error, { component: 'NotificationsStore', action: 'fetchUnreadNotificationsCount' })
      }
    },

    async fetchNotifications({ pagination = false } = {}) {
      try {
        this.notificationsLoading = true

        const { data } = await apiClient().get(`/notifications?page=${ pagination ? this.notifications.current_page + 1 : 1 }`)

        if (pagination) {
          this.notifications = {
            ...data.data,
            data: [ ...this.notifications.data, ...data.data.data ],
          }
        } else {
          this.notifications = data.data
        }

        this.notificationsLoading = false
      } catch (error) {
        handleApiError(error, { component: 'NotificationsStore', action: 'fetchNotifications' })
        this.notificationsLoading = false
      }
    },

    async fetchNotificationsPreviews() {
      try {
        const { data } = await apiClient().get('/notifications/preview')

        if (data && data.jobs && Object.keys(data.jobs).length) {
          this.inprogressJobsWithInfo = data.jobs
        }

        if (data && data.today && Object.keys(data.today).length) {
          this.notificationsPreviews = data.today
        }
       
      } catch (error) {
        handleApiError(error, { component: 'NotificationsStore', action: 'fetchNotificationsPreviews' })
      }
    }
  }
})